import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { AnalyticsEventOutput } from '../analytics/common-analytics.service';
import { AnchoredPosition } from '../callout/callout-position';
import { InputSize } from './text-field.component';

@Component({
  selector: 'date-text-field',
  templateUrl: './date-text-field.component.html',
  styleUrls: ['./date-text-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DateTextFieldComponent),
    },
  ],
})
export class DateTextFieldComponent implements ControlValueAccessor {
  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  @Input() label: string;
  @Input() help: string;
  @Input() required: boolean;
  @Input() invalid: boolean;
  @Input() disabled: boolean;
  @Input() error: boolean;
  _readonly: boolean;
  @Input()
  set readonly(value) {
    this._readonly = value !== false;
  }
  @Input()
  readonly analyticsEvents: {
    setDate?: AnalyticsEventOutput<string>;
  } = {};
  // eslint-disable-next-line
  get readonly(): boolean {
    return this._readonly;
  }

  @Input() min: string | null = null;
  @Input() max: string | null = null;
  @Input() textSize: InputSize = 'medium';
  @Input() clearable: boolean = false;
  @Output() clearDate = new EventEmitter();

  // sanitized value
  value: string;

  // text
  text: string;

  changeText(value: string) {
    // Filter out bad characters and auto-insert slashes. In order to get the value
    // changes we make here to propagate to the view we need to set the value, do
    // our filtering and then set the value again so that change detection actually
    // detects a change.
    value = (value || '').replace(/[^0-9/]/g, '');
    const parts = value.split('/');
    if (parts.length == 1 && value.length == 3) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    }
    if (parts.length == 2 && value.length == 6) {
      value = `${value.slice(0, 5)}/${value.slice(5)}`;
    }
    this.changeDetector.markForCheck();

    if (!/^[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{4}$/.test(value)) {
      this.text = value;
      this.propagateChange(null);
      return;
    }
    let [month, day, year] = parts;
    if (month.length == 1) {
      month = '0' + month;
    }
    if (day.length == 1) {
      day = '0' + day;
    }
    const dateString = `${year}-${zeroPad(month, 2)}-${zeroPad(day, 2)}`;
    const m = moment(dateString);
    if (!m.isValid() || m.format('YYYY-MM-DD') != dateString) {
      this.text = value;
      this.propagateChange(null);
      return;
    }
    if (this.min !== null && m.isBefore(this.min)) {
      this.changeValue(this.min);
      return;
    }
    if (this.max !== null && m.isAfter(this.max)) {
      this.changeValue(this.max);
      return;
    }
    this.changeValue(dateString);
  }

  changeValue(value: string) {
    if (this.analyticsEvents.setDate && this.value != value) {
      this.analyticsEvents.setDate(value);
    }
    this.writeValue(value);
    this.propagateChange(this.value);
  }

  /**
   * Set a valid value
   */
  writeValue(value: string) {
    this.value = value;
    const m = moment(value);
    this.text = m.isValid() && value ? m.format('MM/DD/YYYY') : '';
    this.changeDetector.markForCheck();
  }

  private propagateChange = (_: any) => {};

  registerOnChange(fn: (_: any) => {}) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}

  readonly topAndBottomPositions = [
    AnchoredPosition.BOTTOM_RIGHT,
    AnchoredPosition.BOTTOM_LEFT,
    AnchoredPosition.TOP_RIGHT,
    AnchoredPosition.TOP_LEFT,
  ];

  open = new BehaviorSubject<boolean>(false);

  get classes() {
    return {
      'rv-input': true,
      disabled: this.disabled,
      [this.textSize || 'medium']: true,
    };
  }

  keypress(event: KeyboardEvent) {
    const key = event.key;

    // ignore all other keypresses
    if (/[^0-9/]/.test(key)) {
      event.preventDefault();
    }
  }
}

function zeroPad(s: string, length: number) {
  return s.padStart(length, '0');
}
